import Heading from 'LuxKit/Typography/Heading'
import OfferList from 'components/OfferList/OfferList'
import { FLASH_HOTELS_AND_TAO_OFFERS_TYPES } from 'constants/offerList'
import { ANYWHERE_PLACE_ID, TYPE_PAGE_LIMIT } from 'constants/search'
import { rem } from 'polished'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { RouteComponentProps } from 'react-router'
import { fetchPlaceByName } from 'actions/PlaceActions'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { escapeTypes, restoreCapitalisation } from 'lib/offer/destinationProductPageUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import { urlTransform } from 'lib/string/removeSpaceUtils'
import { HolidayTypeLandingPageHero } from './components/Hero/HolidayTypeLandingPageHero'
import { capitalise } from 'lib/string/stringUtils'

const OfferListContainer = styled(LayoutContainer)`
  margin-top: ${rem(48)};
  margin-bottom: ${rem(48)};
`

type MatchProps = {
  holidayType: string
  country?: string
  cityOrState?: string
}

interface Props extends RouteComponentProps<MatchProps> {
  locationToPlace: Record<string, App.Place>
}

function HolidayTypeLandingPage({ locationToPlace, match }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const country = match.params.country
  const cityOrState = match.params.cityOrState
  let formattedCountry: string | undefined = undefined
  let formattedCityOrState: string | undefined = undefined
  if (country) {
    formattedCountry = country.replace('-', ' ')
  }
  if (cityOrState) {
    formattedCityOrState = cityOrState.replace('-', ' ')
  }

  const holidayType = match.params.holidayType
  const formattedHolidayType = holidayType.replace('-', ' ')
  const transformedHolidayTypes = escapeTypes.map(escapeType => escapeType.replace('-', ' ')).map(escapeType => urlTransform(escapeType))
  const validHolidayType = transformedHolidayTypes.findIndex((escapeType) => escapeType.toLowerCase() === (holidayType).toLowerCase()) > -1

  const restoreCapitalisationForLocation = (restoreCapitalisation(`${formattedCityOrState ? formattedCityOrState + ', ' + formattedCountry : formattedCountry}`))
  const destinationName = formattedCountry ? restoreCapitalisationForLocation : undefined

  useEffect(() => {
    if (country && destinationName) {
      dispatch(fetchPlaceByName(destinationName))
    }
  }, [cityOrState, country, dispatch, destinationName])

  const locationToPlaceLoaded = country && Object.keys(locationToPlace).length !== 0
  const destinationId = locationToPlace[destinationName!]?.id ? locationToPlace[destinationName!]?.id : ANYWHERE_PLACE_ID
  const destinationDisplayName = locationToPlace[destinationName!]?.canonicalName ? locationToPlace[destinationName!]?.canonicalName : locationToPlace[destinationName!]?.name

  const capitalisedHolidayType = capitalise(formattedHolidayType)

  /**
    * Build filters for <OfferList/>
    *
    * @remarks
    * It would be nice to have 'holidayType' typed as string literals to avoid edge cases like this.
    * Filter name of `Family friendly` should be `Family Friendly`
    *
    * This has to be hardcoded because we cannot capitalise each word of a filter name
    * as there are other filters like `Tours and cruises` 🙄
    */
  const filters = useMemo(() => {
    const resultFilterName = capitalisedHolidayType === 'Family friendly' ? 'Family Friendly' : capitalisedHolidayType

    return {
      holidayTypes: validHolidayType ? [resultFilterName] : [],
      destinationId,
      offerTypes: FLASH_HOTELS_AND_TAO_OFFERS_TYPES,
      limit: TYPE_PAGE_LIMIT,
      rooms: [{ adults: 2 }],
    }
  }, [capitalisedHolidayType, destinationId, validHolidayType])

  const heroProps: React.ComponentProps<typeof HolidayTypeLandingPageHero> | undefined = destinationDisplayName ? {
    destinationId,
    destination: destinationDisplayName,
    holidayType: capitalisedHolidayType,
    validHolidayType,
  } : undefined

  return (
    <>
      {heroProps && <HolidayTypeLandingPageHero {...heroProps} />}
      <OfferListContainer>
        {(!country || locationToPlaceLoaded) &&
          <OfferList
            filters={filters}
            interstitials={
              [
                {
                  element: <div>
                    <Heading variant="heading3">
                      {intl.formatMessage({
                        id: 'pV09rQByYp',
                        defaultMessage: 'No offers available',
                        description: 'Heading shown when no offers match selected filters on holiday type landing page',
                      })}
                    </Heading>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: 'XV3NYw8t5e',
                        defaultMessage: 'There are no offers matching your selected filters.',
                        description: 'Message shown when no offers match selected filters on holiday type landing page',
                      })}
                    </BodyText>
                  </div>,
                  id: 'no-offers-message',
                  position: 2,
                  show: 'hasNoOffers',
                },
              ]
            }
          />}
      </OfferListContainer>
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  locationToPlace: state.destination.locationToPlace,
})

export default connect(mapStateToProps)(HolidayTypeLandingPage)
